import { Injectable, OnDestroy } from '@angular/core';
import { init, ChatbotController } from 'chatbot';
import { environment } from 'src/environments/environment';
import { LogService } from '../baseServices/logging/log.service';

/**
 * Service to manage the chatbot widget lifecycle in the Flex application.
 * Initializes the chatbot during app startup and cleans up when app is destroyed.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatbotService implements OnDestroy {
  private chatbotController: ChatbotController | null = null;
  private chatbotInitialized = false;

  constructor(private logService: LogService) {}

  /**
   * Initializes the chatbot widget if the feature flag is enabled.
   * Knowledge base is managed entirely by the backend API.
   */
  initializeChatbot(): void {
    // Check feature flag
    if (!environment.chatbotFeatureFlag) {
      this.logService.info('Chatbot.FeatureDisabled');
      return;
    }

    if (this.chatbotInitialized) {
      this.logService.warn('Chatbot.AlreadyInitialized');
      return;
    }

    try {
      // Get the container element (body or a specific container)
      const container = document.body;

      // Initialize the chatbot (backend API handles all knowledge)
      this.chatbotController = init(
        {
          title: 'RecoveryTrac Assistant',
          ariaLabel: 'RecoveryTrac Assistant Chat',
        },
        container,
      );

      this.chatbotInitialized = true;
      this.logService.info('Chatbot.Initialized');
    } catch (error) {
      this.logService.error('Chatbot.InitializeFailed', error instanceof Error ? error : String(error));
      // Don't throw - allow the app to continue without chatbot
    }
  }

  /**
   * Destroys the chatbot widget and cleans up resources.
   * Called on application shutdown.
   */
  destroyChatbot(): void {
    if (this.chatbotController) {
      this.chatbotController.destroy();
      this.chatbotController = null;
      this.chatbotInitialized = false;
      this.logService.info('Chatbot.Destroyed');
    }
  }

  /**
   * Angular lifecycle hook for cleanup.
   */
  ngOnDestroy(): void {
    this.destroyChatbot();
  }
}
